import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getDocumentation, deleteDocumentation, toggleDocumentationStatus } from '../../../models/documentationModel';

function DocumentationList() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    fetchDocumentation();
  }, [sectionFilter, statusFilter]);

  // Clear selections when page or filters change
  useEffect(() => {
    setSelectedDocs([]);
  }, [currentPage, sectionFilter, statusFilter, searchTerm]);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const response = await getDocumentation({
        section: sectionFilter,
        status: statusFilter,
        search: searchTerm
      });
      
      if (response.status === 1 || response.status === 'success') {
        setDocs(response.data?.documentation || []);
        setSections(response.data?.sections || []);
      } else {
        toast.error('Failed to load documentation');
      }
    } catch (error) {
      console.error('Error fetching documentation:', error);
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocumentation();
  };

  const handleDelete = async (doc) => {
    if (window.confirm(`Are you sure you want to delete "${doc.doc_title}"?`)) {
      setDeleting(doc.doc_id);
      try {
        const response = await deleteDocumentation(doc.doc_id);
        if (response.status === 1 || response.status === 'success') {
          toast.success(`"${doc.doc_title}" deleted successfully`);
          fetchDocumentation();
        } else {
          toast.error(response.message || 'Failed to delete documentation');
        }
      } catch (error) {
        console.error('Error deleting documentation:', error);
        toast.error('Failed to delete documentation');
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleToggleStatus = async (doc) => {
    const newStatus = doc.doc_status != 1;
    setToggling(doc.doc_id);
    try {
      const response = await toggleDocumentationStatus(doc.doc_id, newStatus);
      if (response.status === 1 || response.status === 'success') {
        toast.success(`"${doc.doc_title}" ${newStatus ? 'activated' : 'set to draft'}`);
        // Update local state immediately for responsiveness
        setDocs(prev => prev.map(d => 
          d.doc_id === doc.doc_id ? { ...d, doc_status: newStatus ? 1 : 0 } : d
        ));
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    } finally {
      setToggling(null);
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDocs(paginatedDocs.map(doc => doc.doc_id));
    } else {
      setSelectedDocs([]);
    }
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Bulk actions
  const handleBulkActivate = async () => {
    if (selectedDocs.length === 0) return;
    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of selectedDocs) {
      const doc = docs.find(d => d.doc_id === docId);
      if (doc && doc.doc_status != 1) {
        try {
          const response = await toggleDocumentationStatus(docId, true);
          if (response.status === 1 || response.status === 'success') {
            successCount++;
            setDocs(prev => prev.map(d => 
              d.doc_id === docId ? { ...d, doc_status: 1 } : d
            ));
          } else {
            errorCount++;
          }
        } catch {
          errorCount++;
        }
      }
    }

    setBulkProcessing(false);
    setSelectedDocs([]);
    if (successCount > 0) {
      toast.success(`${successCount} document(s) activated`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} document(s) failed to update`);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedDocs.length === 0) return;
    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of selectedDocs) {
      const doc = docs.find(d => d.doc_id === docId);
      if (doc && doc.doc_status == 1) {
        try {
          const response = await toggleDocumentationStatus(docId, false);
          if (response.status === 1 || response.status === 'success') {
            successCount++;
            setDocs(prev => prev.map(d => 
              d.doc_id === docId ? { ...d, doc_status: 0 } : d
            ));
          } else {
            errorCount++;
          }
        } catch {
          errorCount++;
        }
      }
    }

    setBulkProcessing(false);
    setSelectedDocs([]);
    if (successCount > 0) {
      toast.success(`${successCount} document(s) set to draft`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} document(s) failed to update`);
    }
  };

  // Filter and paginate
  const filteredDocs = docs.filter(doc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      doc.doc_title?.toLowerCase().includes(term) ||
      doc.doc_path?.toLowerCase().includes(term) ||
      doc.doc_section?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredDocs.length / pageSize);
  const paginatedDocs = filteredDocs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Selection computed values (must be after paginatedDocs)
  const isAllSelected = paginatedDocs.length > 0 && paginatedDocs.every(doc => selectedDocs.includes(doc.doc_id));
  const isSomeSelected = selectedDocs.length > 0;

  const getStatusBadge = (status) => {
    const isActive = status == 1;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Active' : 'Draft'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      page: 'bg-blue-100 text-blue-800',
      tooltip: 'bg-yellow-100 text-yellow-800',
      walkthrough: 'bg-purple-100 text-purple-800',
      faq: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${typeColors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Documentation Manager</h1>
                <p className="text-xs text-gray-500 mb-1">Create and manage context-aware documentation for the system</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {!loading && `${filteredDocs.length} documentation pages`}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls */}
            <div className="flex flex-col gap-2 items-end">
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={() => {
                    fetchDocumentation();
                  }}
                  disabled={loading}
                  className={`inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin text-blue-500' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Bulk Actions - Show when items selected */}
                {isSomeSelected && (
                  <>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <span className="text-xs text-gray-500">{selectedDocs.length} selected</span>
                    <button
                      onClick={handleBulkActivate}
                      disabled={bulkProcessing}
                      className="inline-flex items-center px-2.5 py-1.5 border border-green-300 rounded-md shadow-sm text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                      title="Activate selected"
                    >
                      {bulkProcessing ? (
                        <svg className="h-3.5 w-3.5 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      Activate
                    </button>
                    <button
                      onClick={handleBulkDeactivate}
                      disabled={bulkProcessing}
                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
                      title="Set to draft"
                    >
                      {bulkProcessing ? (
                        <svg className="h-3.5 w-3.5 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      )}
                      Draft
                    </button>
                  </>
                )}
                
                {/* Section Filter */}
                <select
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                  className="text-sm px-2 py-1.5 border border-gray-300 rounded-md"
                >
                  <option value="">All Sections</option>
                  {sections.map(section => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </select>
                
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-sm px-2 py-1.5 border border-gray-300 rounded-md"
                >
                  <option value="">All Statuses</option>
                  <option value="1">Active</option>
                  <option value="0">Draft</option>
                </select>

                {/* Create Button */}
                <button
                  onClick={() => navigate('/dashboard/documentation/new')}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white"
                  style={{ backgroundColor: '#0087D4' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Documentation
                </button>
              </div>
              
              {/* Search */}
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search documentation..."
                  className="text-sm px-3 py-1.5 border border-gray-300 rounded-md w-64"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Search
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Documentation Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : paginatedDocs.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No documentation found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new documentation page.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/dashboard/documentation/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Documentation
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-[4%] px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="w-[20%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="w-[18%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                    <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                    <th className="w-[10%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="w-[8%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                    <th className="w-[14%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedDocs.map(doc => (
                    <tr key={doc.doc_id} className={`hover:bg-gray-50 ${selectedDocs.includes(doc.doc_id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.doc_id)}
                          onChange={() => handleSelectDoc(doc.doc_id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate" title={doc.doc_title}>{doc.doc_title}</div>
                        {doc.doc_summary && (
                          <div className="text-xs text-gray-500 truncate" title={doc.doc_summary}>{doc.doc_summary}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded block truncate" title={doc.doc_path}>{doc.doc_path}</code>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 truncate" title={doc.doc_section}>
                        {doc.doc_section || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {getTypeBadge(doc.doc_type)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleStatus(doc); }}
                          disabled={toggling === doc.doc_id}
                          className={`inline-flex items-center px-2 py-1 text-xs rounded-full transition-colors cursor-pointer ${
                            doc.doc_status == 1 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          } ${toggling === doc.doc_id ? 'opacity-50' : ''}`}
                          title={`Click to ${doc.doc_status == 1 ? 'set to draft' : 'activate'}`}
                        >
                          {toggling === doc.doc_id ? (
                            <svg className="h-3 w-3 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          ) : (
                            <span className={`w-2 h-2 rounded-full mr-1 ${doc.doc_status == 1 ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                          )}
                          {doc.doc_status == 1 ? 'Active' : 'Draft'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 truncate" title={doc.author_name}>
                        {doc.author_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/dashboard/documentation/view/${doc.doc_id}`)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="View"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => navigate(`/dashboard/documentation/edit/${doc.doc_id}`)}
                            className="text-indigo-600 hover:text-indigo-900 p-1"
                            title="Edit"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(doc)}
                            disabled={deleting === doc.doc_id}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            {deleting === doc.doc_id ? (
                              <svg className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, filteredDocs.length)}</span> of{' '}
                    <span className="font-medium">{filteredDocs.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                      if (page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentationList;
